import { useBoolean } from "@aurem/ui";
import { useI18n } from "providers/I18nProvider";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { EditTranslationCaret } from "./EditTranslationCaret";
import { EditTranslationModal } from "./EditTranslationModal";
import type { TranslationMatch } from "./parser";
import { useTranslationScanner } from "./useTranslationScanner";

type EditTranslationContextType = {
  isOn: boolean;
  setOn: () => void;
  setOff: () => void;
  toggle: () => void;
};

const EditTranslationContext = createContext<EditTranslationContextType | null>(
  null,
);

export function useEditTranslation() {
  const ctx = useContext(EditTranslationContext);
  if (!ctx)
    throw new Error(
      "useEditTranslation must be used within EditTranslationProvider",
    );
  return ctx;
}

type Props = {
  children: ReactNode;
};

export const EditTranslationProvider = ({ children }: Props) => {
  const {
    value: isOn,
    setTrue: setOn,
    setFalse: setOff,
    toggle,
  } = useBoolean(false);
  const { t } = useI18n();
  const [selectedMatch, setSelectedMatch] = useState<TranslationMatch | null>(
    null,
  );

  const { matches, scanDOM } = useTranslationScanner({
    isOn,
    selectedMatch,
    t: t as (key: string) => string,
  });

  useEffect(() => {
    if (!isOn) {
      setSelectedMatch(null);
    }
  }, [isOn]);

  const handleClose = useCallback(() => {
    setSelectedMatch(null);
    setTimeout(scanDOM, 50);
  }, [scanDOM]);

  const handleSelectMatch = useCallback((match: TranslationMatch | null) => {
    setSelectedMatch(match);
  }, []);

  const contextValue = useMemo(
    () => ({ isOn, setOn, setOff, toggle }),
    [isOn, setOn, setOff, toggle],
  );

  return (
    <EditTranslationContext.Provider value={contextValue}>
      {children}

      {isOn &&
        matches.map((match) => (
          <EditTranslationCaret
            key={match.id}
            match={match}
            isSelected={selectedMatch?.id === match.id}
            onSelect={handleSelectMatch}
          />
        ))}

      {selectedMatch && (
        <EditTranslationModal match={selectedMatch} onClose={handleClose} />
      )}
    </EditTranslationContext.Provider>
  );
};
